import { useEffect, useRef, useCallback } from "react";

/**
 * Returns the base API URL (matches the logic in api/client.js).
 */
function getApiUrl() {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  // Production: served through nginx reverse proxy (default port)
  if (!window.location.port || window.location.port === '80' || window.location.port === '443') {
    return `${window.location.origin}/api`;
  }
  // Local development: connect directly to backend
  return `http://${window.location.hostname}:8000`;
}

/**
 * Hook that opens an SSE connection to /notifications/stream and
 * invokes the provided callbacks whenever chat events arrive.
 *
 * @param {boolean}  enabled    - only connect when true (e.g. user is logged in & has chat.view)
 * @param {function} onChatMessage - called with the parsed event data for `chat_message` events
 * @param {function} [onMigrationChatMessage] - called with the parsed event data for `migration_chat_message` events
 */
export default function useChatNotifications(enabled, onChatMessage, onMigrationChatMessage) {
  const callbackRef = useRef(onChatMessage);
  callbackRef.current = onChatMessage;
  const migrationCallbackRef = useRef(onMigrationChatMessage);
  migrationCallbackRef.current = onMigrationChatMessage;

  // Stable reference so we can force a reconnect from outside
  const sourceRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;

    const url = `${getApiUrl()}/notifications/stream`;
    const es = new EventSource(url, { withCredentials: true });
    sourceRef.current = es;

    es.addEventListener("chat_message", (e) => {
      try {
        const data = JSON.parse(e.data);
        callbackRef.current?.(data);
      } catch {
        // malformed event — ignore
      }
    });

    es.addEventListener("migration_chat_message", (e) => {
      try {
        const data = JSON.parse(e.data);
        migrationCallbackRef.current?.(data);
      } catch {
        // malformed event — ignore
      }
    });

    es.onerror = () => {
      // EventSource will auto-reconnect; nothing to do here.
    };

    return () => {
      es.close();
      sourceRef.current = null;
    };
  }, [enabled]);

  // Return a stable function to force-close (used on logout)
  const disconnect = useCallback(() => {
    if (sourceRef.current) {
      sourceRef.current.close();
      sourceRef.current = null;
    }
  }, []);

  return { disconnect };
}
